import './HouseholdInformationCard.scss';

import React from 'react';
import { isNil } from 'lodash';
import classnames from 'classnames';
import Store from 'state/Store';

import BaseCard from 'components/shared/Card/BaseCard';
import { Dollar } from 'components/shared/Text/Text';
import { Link } from 'react-router-dom';
import { tierTitle } from 'services/HouseholdInformationCard';
import {
  kiplingerExistingSubscriptionLink,
  smpCollegeSavingsProgramInfoLink
} from 'services/Externals';
import { ENHANCED_PERSONAL_SERVICES } from 'services/HouseholdInformationCard';
import { ReactComponent as ChevronRight } from 'assets/icons/chevron-right.svg';
import { ReactComponent as KiplingerLogo } from 'assets/logo/logo-kiplinger.svg';
import { getKiplingerRegistration } from 'services/Rest';
import { MorningstarRegistration } from './MorningstarRegistration';
import ThirdPartyWebsiteModal from 'components/shared/Modal/ThirdPartyWebsiteModal';
import ActionButton from 'components/shared/Buttons/ActionButton';
import {
  showThirdPartyModal,
  targetURL
} from 'state/thirdParty/ThirdPartyModalActions';
import { mmddyyyy } from 'services/Dates';
import { toDelimitedString } from 'support/StringUtil';
import WithUserPermissions from 'components/shared/WithUserPermissions/WithUserPermissions';
import Content, { contentConverter } from 'services/Content';
import { updatedSummitBenefitsEnabled } from 'services/FeatureFlags';

export const kiplingerLink = (
  kiplingerAccess,
  kiplingerRegistrationUrl,
  isCustomerWebView
) => (
  <div className="household-client-link kiplinger-link">
    <KiplingerLogo className="household-kiplinger-logo" />
    <ThirdPartyWebsiteModal />
    <ActionButton
      onClick={() => {
        Store.dispatch(showThirdPartyModal(true));
        Store.dispatch(targetURL(kiplingerExistingSubscriptionLink));
      }}
      id="kiplinger-existing-subscription-id">
      <span>Existing Subscription</span>
      <ChevronRight className="chevron" />
    </ActionButton>
    <a
      className={isCustomerWebView ? 'kiplinger-sign-up-disabled' : ''}
      onClick={() =>
        kiplingerAccess && !isCustomerWebView
          ? getKiplingerRegistration().then((response) => {
              if (response) {
                const {
                  payload: { registrationURL }
                } = response;
                if (registrationURL) {
                  Store.dispatch(targetURL(registrationURL));
                  Store.dispatch(showThirdPartyModal(true));
                }
              }
              return false;
            })
          : null
      }>
      <span>Sign Up For Subscription</span>
      <ChevronRight className="chevron" />
    </a>
  </div>
);

const HouseholdInformationCard = ({ data, isLoading, hasError }) => {
  const {
    assetBalance,
    assetId,
    balanceReportDate,
    styleOverride,
    householdMemberNames,
    isProgramBenefits,
    hasCollegeSavingsOnly,
    kiplingerAccess,
    kiplingerRegistrationUrl,
    isCustomerWebView,
    hasMorningstarAccess,
    showHouseholdDetails,
    showBalance
  } = data;

  const hasHouseholdInformation = !isNil(assetId) || !isNil(assetBalance);

  if (hasError || !hasHouseholdInformation) {
    return null;
  }

  const footerClassName = classnames({
    'household-card-footer': updatedSummitBenefitsEnabled(),
    'household-card-old-footer': !updatedSummitBenefitsEnabled()
  });

  const footer = isProgramBenefits ? null : (
    <div className={footerClassName}>
      <Link to="/program-benefits" className="household-view-benefits-link">
        { updatedSummitBenefitsEnabled() ? 'View My Summit Household & Benefits' : 'View Benefits' }
      </Link>
      {hasCollegeSavingsOnly && (
        <a
          href={smpCollegeSavingsProgramInfoLink}
          className="household-view-benefits-link">
          View Program Info
        </a>
      )}
    </div>
  );

  const HouseholdMemberNames = ({ householdMemberNames }) => (
    <p className="household-members">
      Household member(s): {toDelimitedString(householdMemberNames, 'comma')}
    </p>
  );

  const HouseholdMembersWithPermissions =
    WithUserPermissions(HouseholdMemberNames);

  const showKiplinger = assetId === ENHANCED_PERSONAL_SERVICES;

  return (
    <BaseCard
      title={isProgramBenefits ? null : 'Summit Program'}
      footer={footer}
      isLoading={isLoading}
      toggle={null}
      className={`standard household-information-card-container ${
        styleOverride ? 'style-override' : ''
      }`}>
      <div className="household-information-card">
        {tierTitle(assetId)}
        {showHouseholdDetails && styleOverride && (
          <div>
            <p className="household-card-sub-text">
              As of {mmddyyyy(balanceReportDate, '/')}
            </p>
            {showBalance && (
              <div>
                <p className="summit-balance-title">Summit Balance</p>
                <p className="summit-balance-amount">
                  <Dollar>{assetBalance}</Dollar>
                </p>
              </div>
            )}
            <HouseholdMembersWithPermissions
              householdMemberNames={householdMemberNames}
              shouldMask
            />
            <p
              className="disclosure"
              dangerouslySetInnerHTML={{
                __html: contentConverter.makeHtml(
                  Content.summitBenefitsTileDisclosureCopy
                )
              }}></p>
          </div>
        )}
        {!isProgramBenefits && hasMorningstarAccess && (
          <MorningstarRegistration />
        )}
        {!isProgramBenefits &&
          showKiplinger &&
          kiplingerLink(
            kiplingerAccess,
            kiplingerRegistrationUrl,
            isCustomerWebView
          )}
      </div>
    </BaseCard>
  );
};

export default HouseholdInformationCard;
